import { formatNumber } from '@angular/common';
import { Component, HostListener, Input, OnChanges } from '@angular/core';
import { GalaxyChartType } from '@bit/vendasta.planets.chart';
import { TranslateService } from '@ngx-translate/core';
import { Chart, ChartConfiguration, ChartData, registerables } from 'chart.js';
import dayjs from 'dayjs';
import { MaxMapsRank, MaxRank, SEODataPoint, SEOTableData, SeoDataKeywordMetrics } from '../local-seo';
import { StatTabData } from '../stat-tabs/stat-tabs.component';

interface CardDataPoint {
  value: string;
  delta?: number;
}

interface keywordMovement {
  keywordsUp: number;
  keywordsDown: number;
  ranksUp: number;
  ranksDown: number;
}

interface keywordRanks {
  date: Date;
  top10: number;
  top50: number;
  other: number;
}

interface positionDataPoint {
  date: Date;
  local: positionTotal;
  organic: positionTotal;
  average: positionTotal;
}

interface positionTotal {
  value: number;
  totalKeywords: number;
}

@Component({
  selector: 'local-seo-keywords-stats-cards',
  templateUrl: './local-seo-keywords-stats-cards.component.html',
  styleUrls: ['./local-seo-keywords-stats-cards.component.scss'],
  standalone: false,
})
export class LocalSeoKeywordsStatsCardsComponent implements OnChanges {
  localCards: StatTabData[];
  @Input() data: SEOTableData[];

  keywordMovement: keywordMovement;
  screenWidth = window.innerWidth;

  constructor(private translate: TranslateService) {
    this.onResize();
    Chart.register(...registerables);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.top10ChartConfig.options.plugins.legend.position = window.innerWidth > 600 ? 'right' : 'bottom';
    this.positionChartConfig.options.plugins.legend.position = window.innerWidth > 600 ? 'right' : 'bottom';
  }

  @Input() set keywordMetrics(metrics: SeoDataKeywordMetrics[]) {
    this.setTop10PositionChartData(metrics);
    this.setPositionChartData(metrics);
  }

  setTop10PositionChartData(metrics: SeoDataKeywordMetrics[]) {
    const series = metrics.map((keywordSeries) =>
      getBestRankForEachDate(keywordSeries?.organicRank, keywordSeries?.localRank),
    );
    let values: keywordRanks[] = [];
    series.reduce((acc, keywordSeries) => {
      acc = keywordSeries.reduce((acc2, point) => {
        if (point.value > 0 && point.value <= 10) {
          acc2 = addKeywordRanksToList(acc2, point.date, true, false, false);
        } else if (point.value > 0 && point.value <= MaxRank) {
          acc2 = addKeywordRanksToList(acc2, point.date, false, true, false);
        } else {
          acc2 = addKeywordRanksToList(acc2, point.date, false, false, true);
        }
        return acc2;
      }, acc);
      return acc;
    }, values);

    values = values.sort((a, b) => {
      return new Date(a.date).getTime() < new Date(b.date).getTime() ? -1 : 1;
    });

    this.top10ChartData.labels = values.map((value) => dayjs.utc(value.date).format('MMM D'));
    this.top10ChartData.datasets = [
      {
        label: this.translate.instant('LOCAL_SEO.LOCAL_SEO.GRAPHS.OVER_50'),
        data: values.map((value) => value.other),
        backgroundColor: ['#BDBDBD'],
      },
      {
        label: this.translate.instant('LOCAL_SEO.LOCAL_SEO.GRAPHS.TOP50'),
        data: values.map((value) => value.top50),
        backgroundColor: ['#4CAF50'],
      },
      {
        label: this.translate.instant('LOCAL_SEO.LOCAL_SEO.GRAPHS.TOP10'),
        data: values.map((value) => value.top10),
        backgroundColor: ['#388E3C'],
      },
    ];
  }

  setPositionChartData(metrics: SeoDataKeywordMetrics[]) {
    let datesWithData = metrics.reduce((acc, keywordSeries) => {
      keywordSeries.organicRank.reduce((acc2, point) => {
        if (point.value >= 0 && !acc2.includes(point.date)) {
          acc2.push(point.date);
        }
        return acc2;
      }, acc);
      keywordSeries.localRank.reduce((acc2, point) => {
        if (point.value >= 0 && !acc2.includes(point.date)) {
          acc2.push(point.date);
        }
        return acc2;
      }, acc);
      return acc;
    }, []);

    datesWithData = datesWithData.sort((a, b) => {
      return new Date(a).getTime() < new Date(b).getTime() ? -1 : 1;
    });

    let dataPoints: positionDataPoint[] = datesWithData.map((date) => {
      return {
        date: date,
        local: {
          value: 0,
          totalKeywords: 0,
        },
        organic: {
          value: 0,
          totalKeywords: 0,
        },
        average: {
          value: 0,
          totalKeywords: 0,
        },
      };
    });

    dataPoints = metrics.reduce((acc, keywordSeries) => {
      acc = keywordSeries.organicRank.reduce((acc2, point) => {
        if (point.value === 0 || point.value > MaxRank) {
          point.value = MaxRank + 1;
        }
        const existingIndex = acc.findIndex((entry) => entry.date === point.date);
        if (existingIndex > -1) {
          acc2[existingIndex].organic.value += point.value;
          acc2[existingIndex].organic.totalKeywords++;
          acc2[existingIndex].average.value += point.value;
          acc2[existingIndex].average.totalKeywords++;
        }
        return acc2;
      }, acc);
      acc = keywordSeries.localRank.reduce((acc2, point) => {
        if (point.value === 0 || point.value > MaxMapsRank) {
          point.value = MaxMapsRank + 1;
        }
        const existingIndex = acc.findIndex((entry) => entry.date === point.date);
        if (existingIndex > -1) {
          acc2[existingIndex].local.value += point.value;
          acc2[existingIndex].local.totalKeywords++;
          acc2[existingIndex].average.value += point.value;
          acc2[existingIndex].average.totalKeywords++;
        }
        return acc2;
      }, acc);
      return acc;
    }, dataPoints);

    this.positionChartData.labels = datesWithData.map((date) => dayjs.utc(date).format('MMM D'));
    this.positionChartData.datasets = [
      {
        label: '',
        type: 'line',
        data: dataPoints.map((point) => {
          if (point.average.value === 0 || point.average.totalKeywords === 0) {
            return 0;
          }
          return MaxRank - Math.round(point.average.value / point.average.totalKeywords);
        }),
        borderColor: ['#000000'],
        borderDash: [2, 2],
        borderWidth: 1,
        pointRadius: 0,
      },
      {
        label: this.translate.instant('LOCAL_SEO.LOCAL_SEO.GRAPHS.LOCAL'),
        data: dataPoints.map((point) => {
          if (point.local.value === 0 || point.local.totalKeywords === 0) {
            return 0;
          }
          return MaxRank - Math.round(point.local.value / point.local.totalKeywords);
        }),
        backgroundColor: ['#66BB6A'],
      },
      {
        label: this.translate.instant('LOCAL_SEO.LOCAL_SEO.GRAPHS.ORGANIC'),
        data: dataPoints.map((point) => {
          if (point.organic.value === 0 || point.organic.totalKeywords === 0) {
            return 0;
          }
          return MaxRank - Math.round(point.organic.value / point.organic.totalKeywords);
        }),
        backgroundColor: ['#56B4E9'],
      },
    ];
  }

  top10ChartConfig: ChartConfiguration = {
    type: GalaxyChartType.BAR,
    data: {
      datasets: [],
      labels: [],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: this.screenWidth > 600 ? 'right' : 'bottom',
          align: 'center',
          fullSize: true,
        },
      },
      scales: {
        x: {
          stacked: false,
        },
        y: {
          beginAtZero: true,
          ticks: {
            maxTicksLimit: 3,
          },
        },
      },
    },
  };

  top10ChartData: ChartData = {
    datasets: [],
  };

  positionChartConfig: ChartConfiguration = {
    type: GalaxyChartType.BAR,
    data: {
      datasets: [],
      labels: [],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: this.screenWidth > 600 ? 'right' : 'bottom',
          align: 'center',
          fullSize: true,
          labels: {
            filter: function (item) {
              return !!item.text;
            },
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += MaxRank - context.parsed.y;
              }
              return label;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: false,
        },
        y: {
          max: MaxRank - 1,
          min: 0,
          ticks: {
            maxTicksLimit: 3,
            callback: function (value): string {
              if (value === 0) {
                return `${MaxRank}+`;
              }
              return (-1 * (+value - MaxRank)).toString();
            },
          },
        },
      },
    },
  };

  positionChartData: ChartData = {
    labels: [],
    datasets: [],
  };

  ngOnChanges() {
    const averagePosition = this.getAveragePosition(this.data);
    const top10 = this.getTop10(this.data);
    const hasPreviousData = !!this.data.find((item) => !!item.localStartedAt);
    this.keywordMovement = this.getKeywordsUp(this.data, hasPreviousData);

    this.localCards = [
      {
        name: 'LOCAL_SEO.LOCAL_SEO.TOP_10',
        value: top10.value,
        showDelta: hasPreviousData,
        deltaValue: top10.delta,
      },
      {
        name: 'LOCAL_SEO.LOCAL_SEO.AVERAGE_POSITION',
        value: averagePosition.value,
        showDelta: hasPreviousData,
        deltaValue: averagePosition.delta,
        toolTip: {
          title: 'LOCAL_SEO.LOCAL_SEO.AVERAGE_POSITION_TOOLTIP_TITLE',
          content: 'LOCAL_SEO.LOCAL_SEO.AVERAGE_POSITION_TOOLTIP',
        },
      },
      {
        name: 'LOCAL_SEO.LOCAL_SEO.KEYWORDS_UP',
        value: this.keywordMovement.keywordsUp.toString(),
        showDelta: false,
        deltaValue: null,
        subtitle:
          this.keywordMovement.ranksUp > 0
            ? this.translate.instant('LOCAL_SEO.LOCAL_SEO.BY_RANKS', { value: this.keywordMovement.ranksUp })
            : '',
      },
    ];
  }

  private getAveragePosition(data: SEOTableData[]): CardDataPoint {
    let currentTotalRanks = 0;
    let currentTotalRankValue = 0;
    let previousTotalRanks = 0;
    let previousTotalRankValue = 0;
    data.forEach((item) => {
      const organicValue =
        item.organicRank.rawValue && item.organicRank.rawValue < MaxRank ? item.organicRank.rawValue : MaxRank + 1;
      const localValue =
        item.localRank.rawValue && item.localRank.rawValue < MaxMapsRank ? item.localRank.rawValue : MaxMapsRank + 1;
      const organicStartedAt =
        item.organicStartedAt.rawValue && item.organicStartedAt.rawValue < MaxRank
          ? item.organicStartedAt.rawValue
          : MaxRank + 1;
      const localStartedAt =
        item.localStartedAt.rawValue && item.localStartedAt.rawValue < MaxMapsRank
          ? item.localStartedAt.rawValue
          : MaxMapsRank + 1;
      currentTotalRanks += 2;
      currentTotalRankValue += organicValue;
      currentTotalRankValue += localValue;

      previousTotalRanks += 2;
      previousTotalRankValue += organicStartedAt;
      previousTotalRankValue += localStartedAt;
    });
    if (currentTotalRanks === 0) {
      return { value: '---' };
    }
    const average = Math.round(currentTotalRankValue / currentTotalRanks);
    const previousAverage = Math.round(previousTotalRankValue / previousTotalRanks);
    // We invert the delta here because a lower number is better
    return { value: formatNumber(average, 'en-US', '1.0-2'), delta: -1 * (average - previousAverage) };
  }

  private getKeywordsUp(data: SEOTableData[], hasPreviousData: boolean): keywordMovement {
    if (!hasPreviousData) {
      return {
        ranksUp: 0,
        ranksDown: 0,
        keywordsUp: 0,
        keywordsDown: 0,
      };
    }

    let keywordsUp = 0;
    let keywordsDown = 0;
    let ranksUp = 0;
    let ranksDown = 0;
    data.forEach((item) => {
      let rank = 0;
      if (item.localRank.rawValue > 0 && item.localRank.rawValue < MaxMapsRank && item.localStartedAt.rawValue > 0) {
        rank += item.localStartedAt.rawValue - item.localRank.rawValue;
      }

      if (item.organicRank.rawValue > 0 && item.organicRank.rawValue < MaxRank && item.organicStartedAt.rawValue > 0) {
        rank += item.organicStartedAt.rawValue - item.organicRank.rawValue;
      }
      rank > 0 ? keywordsUp++ : rank < 0 ? keywordsDown++ : null;
      rank > 0 ? (ranksUp += rank) : rank < 0 ? (ranksDown += rank) : null;
    });

    return {
      ranksUp: ranksUp,
      ranksDown: Math.abs(ranksDown),
      keywordsUp: keywordsUp,
      keywordsDown: keywordsDown,
    };
  }

  private getTop10(data: SEOTableData[]): CardDataPoint {
    const currentTop10 = data.filter(
      (item) =>
        (item.localRank.rawValue > 0 && item.localRank.rawValue <= 10) ||
        (item.organicRank.rawValue > 0 && item.organicRank.rawValue <= 10),
    ).length;
    const previousTop10 = data.filter(
      (item) =>
        (item.localStartedAt.rawValue > 0 && item.localStartedAt.rawValue <= 10) ||
        (item.organicStartedAt.rawValue > 0 && item.organicStartedAt.rawValue <= 10),
    ).length;
    return {
      value: formatNumber(currentTop10, 'en-US', '1.0-2'),
      delta: currentTop10 - previousTop10,
    };
  }
}

function addKeywordRanksToList(
  list: keywordRanks[],
  date: Date,
  top10: boolean,
  top50: boolean,
  other: boolean,
): keywordRanks[] {
  const existingIndex = list.findIndex((point) => point.date === date);
  if (existingIndex > -1) {
    if (top10) {
      list[existingIndex].top10++;
    }
    if (top50) {
      list[existingIndex].top50++;
    }
    if (other) {
      list[existingIndex].other++;
    }
  } else {
    list.push({
      date: date,
      top10: top10 ? 1 : 0,
      top50: top50 ? 1 : 0,
      other: other ? 1 : 0,
    });
  }
  return list;
}

function getBestRankForEachDate(organic: SEODataPoint[], local: SEODataPoint[]) {
  const bestRank: SEODataPoint[] = [];
  organic.forEach((organicPoint) => {
    bestRank.push({
      date: organicPoint.date,
      value: organicPoint.value,
    });
  });
  local.forEach((localPoint) => {
    const existingIndex = bestRank.findIndex((point) => point.date === localPoint.date);
    if (existingIndex > -1) {
      if (
        (localPoint.value !== 0 && localPoint.value < bestRank[existingIndex].value) ||
        bestRank[existingIndex].value === 0
      ) {
        bestRank[existingIndex].value = localPoint.value;
      }
    } else {
      bestRank.push({
        date: localPoint.date,
        value: localPoint.value,
      });
    }
  });
  return bestRank;
}
